import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactTable } from "../../Molecules";
import { VIEW_VEHICLES_HEADERS } from "../../../Library/tableHeaders";

import "./style.css";
import { getFilters } from "../../../Library/tableHelpers";
import { Link } from "react-router-dom";
import PATHS from "../../../Routes/paths";
import { getLocalisedDate, getLocalisedTime } from "../../../Library/datetime";
import { DownloadIcon, LocationIcon } from "../../../Assets/images/svgs";
import { jsonToCSVConvertor } from "../../../Library/jsonUtils";
import { ellipsisify, getItemFromLocalStorage, tsWithin10Mins } from "../../../Library/helpers";
import { Tooltip } from "@chakra-ui/react";
import { DEVICE_HWTYPS, VOLTAGE_LEVEL_MAP } from "../../../Library/constants";

const VehiclesList = ({ allVehicles, tableHeaders, vehicleStats, highlightedVehicle, dealers, isSellerOrDealer, showVehOnMapIcon }) => {
  const vehStatsRef = useRef(vehicleStats);
  const isUser = useRef(getItemFromLocalStorage());
  const [ forceRerender, setForceRerender ] = useState(0);
  const [ vehStats, setVehStats ] = useState(new Map());

  const addTooltip = (label, value) => {
    return (
      <Tooltip label={label}>
        {value}
      </Tooltip>
    );
  }

  const locateVehicleOnMap = (vehicle) => {
    let vehData = { vid: vehicle.idx };
    global.EE.emitEvent(global.EE.EVENT_TYPES.MAP_ICON_CLICKED, vehData);

    if (vehicle.ping && vehicle.ping.lat && vehicle.ping.lng) {
      vehData.lat = vehicle.ping.lat;
      vehData.lng = vehicle.ping.lng;
      vehData.zoom = true;

      global.EE.emitEvent(global.EE.EVENT_TYPES.VEHICLE_INFO_SHOWN, vehData);
    }
  }

  const getVehBattery = (veh, showTooltip) => {
    const vehicle = vehStatsRef.current.get(veh?.idx);
    if (vehicle && vehicle.veh && (vehicle.veh.hwtyp || (vehicle.veh.dev && vehicle.veh.dev.hwtyp))) {
      if ((vehicle.veh.hwtyp === DEVICE_HWTYPS.Wired || (vehicle.veh.dev && vehicle.veh.dev.hwtyp === DEVICE_HWTYPS.Wired)) && vehicle.mainPower) {
        return vehicle.vepe ? (showTooltip ? addTooltip("Vehicle's battery voltage", `${vehicle.vepe/1000}V`) : `${vehicle.vepe/1000}V`) : "-";
      } else if (vehicle.veh.hwtyp === DEVICE_HWTYPS.Lite) {
        return (vehicle.heartbeat && vehicle.heartbeat.externalVolt) ? (showTooltip ? addTooltip("Vehicle's battery voltage", `${vehicle.heartbeat.externalVolt}V`) : `${vehicle.heartbeat.externalVolt}V`) : "-";
      } else if (vehicle.veh.hwtyp === DEVICE_HWTYPS.Wireless) {
        if (vehicle.heartbeat) {
          if (vehicle.heartbeat.voltLvlInPercent) return showTooltip ? addTooltip("Device's battery percentage", `${vehicle.heartbeat.voltLvlInPercent}%`) : `${vehicle.heartbeat.voltLvlInPercent}%`;
          else if (vehicle.heartbeat.voltLvl) {
            return showTooltip ? addTooltip(`${VOLTAGE_LEVEL_MAP[vehicle.heartbeat.voltLvl]}. Battery percentage is not available at the moment`, ellipsisify(VOLTAGE_LEVEL_MAP[vehicle.heartbeat.voltLvl], 12)) : VOLTAGE_LEVEL_MAP[vehicle.heartbeat.voltLvl];
          }
        } else return "-";
      }
    }
  }

  const staticColumns = useMemo(() => Object.values(tableHeaders || VIEW_VEHICLES_HEADERS)
    .filter(header => !["ignition", "spd", "status", "ts"].includes(header.key))
    .map(header => ({
      Header: header.nm,
      accessor: header.key,
      disableSortBy: !header.sorting,
      Cell: (row) => {
        if (header.navigate) {
          if (header.key === "dlrId") {
            const dealer = dealers.find(dlr => dlr.idx === row.value);
            return dealer?.nm ? (
              <Tooltip label={dealer.nm.length > 10 ? dealer.nm : ""}>
                <Link className="text-blue" to={`${PATHS.SHOW_DEALERS}/${PATHS.SPECIFIC_DEALER}?idx=${row.value}`}>
                  {ellipsisify(dealer.nm, 10)}
                </Link>
              </Tooltip>
            ) : (
              <p>-</p>
            );
          }

          let navigate = `${PATHS.LIVE_TRACKING}/${PATHS.VEHICLE_STATS}?idx=${row.row.original.idx}&lt=true`;
          if (isSellerOrDealer) navigate = `${PATHS.LIVE_TRACKING}/${PATHS.SPECIFIC_DEVICE}?imei=${row.value}&idx=${row.row.original.idx}&lt=true&showButtons=true&liveTracking=true`;
          return <Link className="text-blue" to={navigate}>{row.value}</Link>;
        }

        if (header.key === "bId") {
          return <Link className="text-blue" to={`${PATHS.BATTERIES}/${row.value}`}>{row.value}</Link>;
        }

        if (header.key === "heartbeat") {
          return getVehBattery(row.row.original, true) || "-";
        }

        if (header.key === "mapIcon") {
          const vehicle = vehStatsRef.current.get(row.row.original.idx);
          if (vehicle) {
            return (
              <div onClick={() => locateVehicleOnMap(row.row.original)} className="locate-vehicle-icon">
                <LocationIcon />
              </div>
            )
          } else return "-";
        }

        return row.value || "-";
      },
      ...getFilters(header),
      disableFilters: header.hideSearch,
    })), [ tableHeaders, dealers ]);

    const handleSorting = (r1, r2, header) => {
      const veh1Id = r1.original.idx;
      const veh2Id = r2.original.idx;
      const veh1Stats = vehStats?.get(veh1Id) || vehStatsRef.current?.get(veh1Id) || { spd: 0, ts: 0, ignition: 0 };
      const veh2Stats = vehStats?.get(veh2Id) || vehStatsRef.current?.get(veh2Id) || { spd: 0, ts: 0, ignition: 0 };

      if (header === "ts") return veh1Stats.ts - veh2Stats.ts;
      if (header === "ignition") return veh1Stats.ignition - veh2Stats.ignition;
      if (header === "spd") {
        const veh1Speed = (veh1Stats.ignition && veh1Stats.spd) ? veh1Stats.spd : 0;
        const veh2Speed = (veh2Stats.ignition && veh2Stats.spd) ? veh2Stats.spd : 0;
        return veh1Speed - veh2Speed;
      }
    };

    const getCellRenderVal = (row, header) => {
      const vehicle = vehStats?.get(row.row.original.idx) || vehStatsRef.current?.get(row.row.original.idx);
      row.row.original.ping = vehicle;

      if (header.key === "ignition") {
        return vehicle ? (vehicle.ignition ? "ON" : "OFF") : "-";
      }

      if (header.key === "spd") {
        return vehicle ? ((vehicle.spd || vehicle.spd === 0) && vehicle.spd.toFixed(0)) : "-";
      }

      if (header.key === "status") {
        if (vehicle) {
          if (!vehicle.mainPower && ((vehicle.temp && !vehicle.heartbeat) || (vehicle.heartbeat && vehicle.heartbeat.mainPower === false))) return "Disconnected";
          if (isUser.current) {
            if (vehicle.ignition && (vehicle.ts && tsWithin10Mins(vehicle.ts)) && vehicle.spd > 5) return "Running";
            return "Idle";
          } else {
            if ((!vehicle.ignition && !vehicle.motion) || (vehicle.heartbeat && !vehicle.ignition && vehicle.heartbeat.mainPower)) return "Idle";
            return "Running";
          }
        } else {
          return "-";
        }
      }

      if (header.key === "ts") {
        return vehicle ? <div className="live-tacking-last-seen-col"><p>{getLocalisedDate(vehicle.ts * 1000, true)}</p> <p>{getLocalisedTime(vehicle.ts * 1000, true, null, true)}</p></div> : "-";
      }

      return row.value || "-";
    }


  let dynamicColumns = useMemo(() => Object.values(tableHeaders || VIEW_VEHICLES_HEADERS)
    .filter(header => ["ignition", "spd", "status", "ts"].includes(header.key))
    .map(header => ({
        Header: header.nm,
        label: header.label,
        accessor: header.key,
        disableSortBy: !header.sorting,
        Cell: null,
        filter: (rows, _, filterValue) => {
          return rows.filter((row) => {
            const rowValue = `${getCellRenderVal({row: row}, { key: header.key })}`;
            return rowValue.toLowerCase().includes(filterValue.toLowerCase())
          });
        }
      })), [ tableHeaders, vehStats ]);

  useEffect(() => {
    vehStatsRef.current = vehicleStats;
    if (vehStatsRef.current && vehStatsRef.current.size >= 1 && forceRerender < 1) setForceRerender(1);
  }, [vehicleStats]);

  dynamicColumns.forEach(column => {
    column.Cell = (row) => getCellRenderVal(row, { key: row.column.id });
    column.sortType = (r1, r2, _) => handleSorting(r1, r2, column.accessor);
  });

  const columns = useMemo(() => {
    const staticWithoutLast = staticColumns.slice(0, -1);
    const lastStaticColumn = staticColumns[staticColumns.length - 1];
    return [...staticWithoutLast, ...dynamicColumns, lastStaticColumn];
  }, [staticColumns, dynamicColumns]);

  const handleVehicleReportDownload = (data) => {
    const getDealerName = (veh) => dealers.find(dlr => dlr.idx === veh?.dlrId)?.nm || "-";

    const getVehStatus = (veh) => {
      const vehicle = vehStats?.get(veh.idx) || vehStatsRef.current?.get(veh.idx);
      if (!vehicle) return "-";

      const { mainPower, temp, heartbeat, ignition, ts, spd } = vehicle;
      if (!mainPower && ((temp && !heartbeat) || (heartbeat?.mainPower === false))) return "Disconnected";
      if (ignition && spd > 5 && ts && tsWithin10Mins(ts)) return "Running";
      return "Idle";
    };

    const sendData = data.map((veh, idx) => {
      const vehStat = vehStats?.get(veh.idx) || vehStatsRef.current.get(veh.idx);
      const timestamp = vehStat?.ts ? vehStat.ts * 1000 : null;

      let data = {
        "Sr. No.": idx + 1,
        "Vehicle Reg": veh?.reg || "-",
        "Device Imei": veh?.dev?.imei || "-",
        "Date of mapping": (veh?.dev && veh?.dev.dm) ? getLocalisedDate(veh.dev.dm) : "-",
        "Last Seen": timestamp ? `${getLocalisedDate(timestamp)} ${getLocalisedTime(timestamp)}` : "-",
        "Ignition": vehStat ? (vehStat.ignition ? "ON" : "OFF") : "-",
        "Current Speed": (vehStat?.ignition && vehStat?.spd && vehStat?.ts && tsWithin10Mins(vehStat.ts)) ? vehStat.spd.toFixed(0) : 0,
        "Status": getVehStatus(veh) || "-",
        "Battery": getVehBattery(veh) || "-",
        "Latitude": vehStat?.lat ? vehStat?.lat.toFixed(5) : "-",
        "Longitude": vehStat?.lng ? vehStat?.lng.toFixed(5) : "-",
      };

      if (isSellerOrDealer) data["Dealer"] = getDealerName(veh);

      return data;
    });

    if (sendData.length) jsonToCSVConvertor(sendData, "", true, `Vehicles_${Date.now()}`, false);
  };

  const handleVehicleStats = (data) => {
    if (data) {
      let dataMap = new Map(data);
      vehStatsRef.current = dataMap;
      setVehStats(dataMap);
    }
  }

  useEffect(() => {
    global.EE.addEventListener(global.EE.EVENT_TYPES.LIVE_TRACKING_VEH_STATS, handleVehicleStats);
    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.LIVE_TRACKING_VEH_STATS, handleVehicleStats);
    }
  }, []);

  const handlePageChange = (visibleData) => {
    global.EE.emitEvent(global.EE.EVENT_TYPES.LT_DATA_SORTED, visibleData);
  }

  return (
    <div className={highlightedVehicle && highlightedVehicle.idx ? "highlight-vehicle" : ""}>
      <ReactTable
        key={highlightedVehicle ? highlightedVehicle.idx : "normal"}
        columns={columns}
        data={allVehicles}
        showSearch={true}
        showOverlay={false}
        heading="Vehicles"
        pageSize={20}
        buttonName={<>
          <DownloadIcon />
          Vehicle Report
        </>}
        buttonHandler={() => handleVehicleReportDownload(allVehicles)}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default VehiclesList;